import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Sparkles } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import ScrollReveal from "@/components/ScrollReveal";
import Marquee from "@/components/Marquee";
import ServicesHero from "@/components/hero/ServicesHero";

const Services = ({ onOpenAudit }) => {
  const { t, lang } = useLang();

  return (
    <main data-testid="services-page" className="bg-brand-bg text-white pt-32">
      <ServicesHero onOpenAudit={onOpenAudit} />

      <Marquee items={t.marquee} variant="gradient" />

      {/* List */}
      <section id="services-list" className="py-20 md:py-28 border-b border-brand-border">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 space-y-5">
          {t.services.list.map((s, i) => (
            <ScrollReveal
              key={s.num}
              delay={i * 50}
              className="grid md:grid-cols-12 gap-8 rounded-2xl border border-brand-border p-8 md:p-12 bg-brand-surface/60 hover:bg-brand-surface transition-colors group card-glow bento-cell"
            >
              <div className="md:col-span-2">
                <span className="font-mono text-xs uppercase tracking-[0.25em] text-brand-purpleLight">
                  {s.num}
                </span>
              </div>
              <div className="md:col-span-5">
                <h2 className="font-heading text-3xl md:text-4xl font-black uppercase leading-tight tracking-tight mb-4">
                  {s.title}
                </h2>
                <p className="text-zinc-400 leading-relaxed">{s.desc}</p>
              </div>
              <div className="md:col-span-4">
                <ul className="space-y-3">
                  {s.bullets.map((b, j) => (
                    <li key={j} className="flex items-start gap-3 text-sm text-zinc-200">
                      <span className="mt-1 w-4 h-4 rounded-full bg-gradient-to-br from-brand-purple to-brand-pink grid place-items-center shrink-0">
                        <Check className="w-2.5 h-2.5 text-white" strokeWidth={3.5} />
                      </span>
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="md:col-span-1 flex md:justify-end items-start">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand-purple/20 to-brand-pink/20 grid place-items-center group-hover:from-brand-purple group-hover:to-brand-pink transition-colors">
                  <ArrowUpRight className="w-5 h-5 text-brand-purpleLight group-hover:text-white group-hover:rotate-45 transition-transform" />
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </section>

      {/* Audit CTA strip */}
      <section className="relative border-b border-brand-border py-20 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="orb orb-pink w-[600px] h-[600px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-20" />
        </div>
        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-brand-purpleLight mb-3 inline-flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" /> {lang === "bg" ? "5 МИНУТИ • БЕЗПЛАТНО" : "5 MIN • FREE"}
            </p>
            <h3 className="font-heading text-3xl md:text-5xl font-black uppercase leading-[0.95] tracking-tighter">
              {lang === "bg" ? (
                <>Не си сигурен какво ти трябва? <span className="text-white">Стартирай AI audit.</span></>
              ) : (
                <>Not sure where to start? <span className="text-white">Run an AI audit.</span></>
              )}
            </h3>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <button
              data-testid="services-page-audit-cta"
              onClick={onOpenAudit}
              className="inline-flex items-center gap-2 btn-holo text-white font-bold uppercase tracking-wide px-6 py-5 rounded-full text-xs"
            >
              <Sparkles className="w-4 h-4" />
              {lang === "bg" ? "Безплатен Audit" : "Free Audit"}
              <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
            </button>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="relative py-24 md:py-32 border-b border-brand-border bg-brand-bgSecond overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="orb orb-purple w-[400px] h-[400px] top-0 left-1/3 opacity-15 animate-float-slow" />
        </div>
        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-brand-purpleLight mb-6">
              {t.pricing.tag}
            </p>
            <h2 className="font-heading text-4xl md:text-6xl font-black uppercase leading-[0.95] tracking-tighter mb-6">
              {t.pricing.heading}
            </h2>
            <p className="text-zinc-400">{t.pricing.sub}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {t.pricing.plans.map((p, i) => {
              const popular = i === 1;
              return (
                <ScrollReveal
                  key={p.name}
                  delay={i * 100}
                  className={`relative rounded-2xl p-8 md:p-10 bento-cell ${
                    popular
                      ? "gradient-border bg-brand-surface shadow-[0_0_60px_-10px_rgba(168,85,247,0.45)]"
                      : "border border-brand-border bg-brand-surface/60 card-glow"
                  }`}
                >
                  {popular && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 btn-holo text-white text-[10px] font-mono uppercase tracking-[0.25em] px-4 py-1.5 rounded-full">
                      {t.pricing.mostPopular}
                    </span>
                  )}
                  <h3 className="font-heading text-3xl font-black uppercase tracking-tight mb-2">
                    {p.name}
                  </h3>
                  <p className="text-sm text-brand-purpleLight font-mono mb-4">{p.tagline}</p>
                  <p className="text-sm text-zinc-400 leading-relaxed mb-8 min-h-[60px]">{p.desc}</p>
                  <ul className="space-y-3 mb-10 min-h-[200px]">
                    {p.features.map((f, j) => (
                      <li key={j} className="flex items-start gap-3 text-sm text-zinc-200">
                        <span className="mt-1 w-4 h-4 rounded-full bg-gradient-to-br from-brand-purple to-brand-pink grid place-items-center shrink-0">
                          <Check className="w-2.5 h-2.5 text-white" strokeWidth={3.5} />
                        </span>
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    data-testid={`services-pricing-cta-${p.name.toLowerCase()}`}
                    className={`inline-flex w-full items-center justify-center gap-2 px-7 py-4 text-sm font-bold uppercase tracking-wide rounded-full transition-colors ${
                      popular ? "btn-holo text-white" : "btn-outline-glow text-white"
                    }`}
                  >
                    {t.pricing.cta}
                    <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
                  </Link>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>
    </main>
  );
};

export default Services;
